import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import Input from '../../../components/atoms/input';
import { useState } from 'react';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import { useTranslation } from 'react-i18next';
import Label from '../../../components/atoms/text/label';
import SVGIcon from '../../../components/atoms/icons';
import classNames from 'classnames';
import Text from '../../../components/atoms/text';
import Button from '../../../components/atoms/button';
import UserService from '../../../services/User.service';

interface ProfileForm {
	name: string;
	email: string;
	password: string;
	confirmPassword: string;
	isMinLength: boolean;
	containsUppercase: boolean;
	containsLowercase: boolean;
	containsNumber: boolean;
	containsSpecialCharacter: boolean;
}

const Profile = () => {
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;
	const { t } = useTranslation();

	const [error, setError] = useState<string>('');
	const [form, setForm] = useState<ProfileForm>(() => ({
		name: user.name || '',
		email: user.email || '',
		password: '',
		confirmPassword: '',
		isMinLength: false,
		containsUppercase: false,
		containsLowercase: false,
		containsNumber: false,
		containsSpecialCharacter: false,
	}));

	const validate = () => {
		if (!form.name || !form.email) {
			NotificationToast.Warn(t('alert.error.mandatory'));
			return;
		}
		if (form.password !== form.confirmPassword) {
			NotificationToast.Warn(t('alert.error.password_compare'));
			return;
		}
		if (
			form.password !== '' &&
			(!form.isMinLength ||
				!form.containsLowercase ||
				!form.containsNumber ||
				!form.containsSpecialCharacter ||
				!form.containsUppercase)
		) {
			NotificationToast.Warn(t('alert.error.password_validation'));
			return;
		}
		return onEdit();
	};

	const validatePassword = (input: string) => ({
		isMinLength: input.length >= 8,
		containsUppercase: /[A-Z]/.test(input),
		containsLowercase: /[a-z]/.test(input),
		containsNumber: /\d/.test(input), // ou [0-9]
		containsSpecialCharacter: /[!@#$%^&*(),.?":{}|<>]/.test(input),
	});

	const renderValidation = (value: boolean, label: string) => (
		<Label className="text-primary flex items-center gap-1">
			<div className="relative size-3 border border-gray rounded-full">
				<SVGIcon
					className={classNames(
						'absolute size-2 stroke-green left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
						{
							hidden: !value,
						}
					)}
					icon="Check"
				/>
			</div>
			{label}
		</Label>
	);

	const onEdit = async () => {
		const response = await UserService.edit(
			{
				name: form.name,
				email: form.email,
				password: form.password !== '' ? form.password : undefined,
				id: user.id,
			},
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('admin.user')} ${t('alert.success.update')}`
		);
		window.location.reload();
	};

	const handlePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
		const validations = validatePassword(e.target.value);
		setForm((prev) => ({
			...prev,
			...validations,
			password: e.target.value,
		}));
	};

	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		setForm((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	return (
		<div className="flex-grow bg-white rounded-lg shadow-md p-8 gap-4 flex justify-around flex-wrap w-full">
			<div className="flex flex-col gap-4 w-96">
				<Input
					name="name"
					label={t('misc.name')}
					placeholder={t('misc.name')}
					value={form.name}
					onChange={handleInput}
					required
				/>

				<Input
					name="email"
					label={t('misc.email')}
					placeholder={t('misc.email')}
					value={form.email}
					onChange={handleInput}
					required
				/>

				<Input
					name="password"
					type="password"
					label={t('misc.password')}
					placeholder={t('misc.password')}
					value={form.password}
					onChange={handlePassword}
				/>

				<Input
					name="confirmPassword"
					type="password"
					label={t('misc.confirm_password')}
					placeholder={t('misc.confirm_password')}
					value={form.confirmPassword}
					onChange={handleInput}
				/>
				<Button onClick={() => validate()}>{t('misc.save')}</Button>
			</div>
			<div className="flex flex-col gap-1 justify-start">
				<Text className="text-primary">{t('login.security')}</Text>
				{renderValidation(form.isMinLength, t('login.min_length'))}
				{renderValidation(
					form.containsUppercase,
					t('login.contains_uppercase')
				)}
				{renderValidation(
					form.containsLowercase,
					t('login.contains_lowercase')
				)}
				{renderValidation(
					form.containsNumber,
					t('login.contains_number')
				)}
				{renderValidation(
					form.containsSpecialCharacter,
					t('login.contains_special')
				)}
			</div>
		</div>
	);
};

export default Profile;
